import React, { useEffect, useState, useMemo } from 'react';
import { isValid } from 'date-fns';
import JsonQuery from 'json-query';
import { baseComponents, hooks as commonHooks } from '@sitebuilder/common';

interface DateFilterProps {
    data: any;
    onChangeDateRange: (minDate: any, maxDate: any) => void;
    staticDataTabIndex?: number;
    minDate: any;
    maxDate: any;
    extremes?: Highcharts.AxisSetExtremesEventObject;
}

const CustomDateRangePicker: React.FC<
    baseComponents.filters.DateFilterOptions & DateFilterProps
> = (props) => {
    const { data, startDatelabel, endDatelabel, ...rest } = props;

    const [startDate, setStartDate] = useState<any>(() => {
        const minDate = new Date(rest?.minDate);
        minDate.setDate(minDate.getDate() + 1);
        return minDate;
    });
    const [endDate, setEndDate] = useState<any>(() => {
        const maxDate = new Date(rest?.maxDate);
        maxDate.setDate(maxDate.getDate() + 1);
        return maxDate;
    });
    const [minDate, setMinDate] = useState<any>(rest?.minDate || null);
    const [maxDate, setMaxDate] = useState<any>(rest?.maxDate || null);
    const i18n = commonHooks.useLocalization();

    // Extract the dates from the provided data using json-query
    const dates = useMemo(
        () => JsonQuery(rest?.dataKey || '', { data }).value || [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rest?.dataKey]
    );

    // Update the start and end date when the dates data changes
    useEffect(() => {
        if (dates?.length > 0) {
            let minDate = new Date(dates?.[0]);
            let maxDate = new Date(dates?.[0]);

            dates?.forEach((date: string) => {
                const currentDate = new Date(date);
                if (isValid(currentDate)) {
                    if (currentDate < minDate) {
                        minDate = currentDate;
                    }
                    if (currentDate > maxDate) {
                        maxDate = currentDate;
                    }
                }
            });

            if (isValid(minDate) && isValid(maxDate)) {
                setMinDate(minDate);
                setStartDate(minDate);
                setMaxDate(maxDate);
                setEndDate(maxDate);
            }
        }
    }, [dates]);

    const onDateChange = (startDate: any, endDate: any) => {
        if (startDate && isValid(startDate) && endDate && isValid(endDate)) {
            const _startDate = {
                day: startDate.getDate(),
                month: startDate.getMonth(),
                year: startDate.getFullYear(),
            };

            const _endDate = {
                day: endDate.getDate(),
                month: endDate.getMonth(),
                year: endDate.getFullYear(),
            };
            rest.onChangeDateRange(_startDate, _endDate);
        }
    };

    const limitedDates = useMemo(
        () => JsonQuery(rest?.limitDatekeySelection || '', { data }).value || [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rest?.limitDatekeySelection]
    );

    useEffect(() => {
        if (rest.extremes && rest.extremes.userMin && rest.extremes.userMax) {
            const userMinDate = new Date(rest.extremes.userMin);
            const userMaxDate = new Date(rest.extremes.userMax);
            if (userMinDate && userMaxDate) {
                setStartDate(() => {
                    const minDate = userMinDate;
                    minDate.setDate(userMinDate.getDate() + 1);
                    return minDate;
                });
                setEndDate(() => {
                    const maxDate = userMaxDate;
                    maxDate.setDate(userMaxDate.getDate() + 1);
                    return maxDate;
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rest.extremes?.userMin, rest.extremes?.userMax]);

    return (
        <div className="date-filter-container">
            <div className="start-date-container">
                <span tabIndex={-1}>{i18n.format(startDatelabel, data)}</span>
                <baseComponents.customDatePicker.CustomDatePicker
                    {...rest}
                    key={startDate}
                    id="startDateInput"
                    minDate={rest.limitStartDateMinToMinDate ? minDate : null}
                    maxDate={
                        rest?.limitCurrentDaySelection
                            ? new Date(new Date().getTime() - 24 * 60 * 60 * 1000)
                            : rest.limitStartDateMaxToCurrentDate
                              ? new Date()
                              : rest?.limitStartDateMaxToMaxDate
                                ? maxDate
                                : null
                    }
                    selected={startDate}
                    onChange={(date: any) => {
                        onDateChange(date, endDate);
                        // setStartDate(date);
                    }}
                    startDate={startDate}
                    endDate={endDate}
                    isStartDate={true}
                    startDateErrorMessage={i18n.format(rest?.startDateErrorMessage, data)}
                    closeOnScroll
                    formatWeekDay={rest.numberOfLettersForTheDays as any}
                    limitedDates={limitedDates}
                    limitDateStartErrorMessage={i18n.format(rest?.limitDateStartErrorMessage, data)}
                    data={data}
                    staticDataTabIndex={rest.staticDataTabIndex}
                />
            </div>
            <div className="end-date-container">
                <span tabIndex={-1}>{i18n.format(endDatelabel, data)}</span>
                <baseComponents.customDatePicker.CustomDatePicker
                    {...rest}
                    key={endDate}
                    id="endDateInput"
                    selected={endDate}
                    minDate={rest.limitEndDateMinToStartDate ? startDate : null}
                    maxDate={
                        rest?.limitCurrentDaySelection
                            ? new Date(new Date().getTime() - 24 * 60 * 60 * 1000)
                            : rest?.limitEndDateMaxToCurrentDate
                              ? new Date()
                              : rest?.limitEndDateMaxToMaxDate
                                ? maxDate
                                : null
                    }
                    onChange={(date: any) => {
                        onDateChange(startDate, date);
                        setEndDate(date);
                    }}
                    isEndDate={true}
                    endDate={endDate}
                    startDate={startDate}
                    endDateErrorMessage={i18n.format(rest?.endDateErrorMessage, data)}
                    closeOnScroll
                    formatWeekDay={rest.numberOfLettersForTheDays as any}
                    limitedDates={limitedDates}
                    limitDateEndErrorMessage={i18n.format(rest?.limitDateEndErrorMessage, data)}
                    data={data}
                    staticDataTabIndex={rest.staticDataTabIndex}
                />
            </div>
        </div>
    );
};

export default CustomDateRangePicker;
